import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Paper, 
  Alert,
  useTheme,
  useMediaQuery,
  Box, 
  Typography, 
  Button
} from '@mui/material';
import { AdminPanelSettings, PersonAdd, Security } from '@mui/icons-material';
import useAuth from '../hooks/useAuth';
import { APP_NAME } from '../App';

// Using Ailyce's login image
const loginImageUrl = 'https://infanion.lienion.com/Loginimg.png';
const logoUrl = '/logo-gray.svg';

/**
 * Register Page
 * Redirects to RequestAccess since direct registration is disabled
 */
const Register = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Redirect to projects if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/projects', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleGoToLogin = () => {
    navigate('/login');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        backgroundColor: '#f5f5f5',
      }}
    >
      {/* Left section - Brand */}
      {!isMobile && (
        <Box
          sx={{
            flex: '0 0 58.33%', // col-7 (7/12 = 58.33%)
            backgroundImage: `url(${loginImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div className="login_bg_overlay__PPsaZ"></div>
          <Box sx={{ position: 'relative', textAlign: 'center', color: 'white', px: 6, zIndex: 1 }}>
            {/* Welcome text removed as requested */}
          </Box>
        </Box>
      )}

      {/* Right section - Registration form */}
      <Box
        sx={{
          flex: isMobile ? '1' : '0 0 41.67%', // col-5 (5/12 = 41.67%)
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, md: 4 },
          backgroundColor: '#fff',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: isMobile ? '100%' : '420px',
            p: 4,
            borderRadius: '4px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            border: 'none',
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <img 
              src={logoUrl} 
              alt="Tremel AI Logo" 
              style={{ height: '40px', marginBottom: '16px' }}
              onError={(e) => {
                e.target.style.display = 'none';
                // Show text fallback if image fails to load
                document.getElementById('register-logo-text').style.display = 'block';
              }}
            />
            <Typography 
              id="register-logo-text" 
              variant="h4" 
              component="div" 
              fontWeight="bold" 
              color={theme.palette.primary.main} 
              sx={{ display: 'none' }}
            >
              {APP_NAME}
            </Typography>
          </Box>
          
          <Typography 
            variant="h5" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            textAlign="center"
            sx={{ 
              mb: 3, 
              color: 'var(--color-blue) !important' 
            }}
          >
            Registration Not Available
          </Typography>

          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Security sx={{ fontSize: 64, color: theme.palette.primary.main, mb: 2 }} />
          </Box>
          
          <Alert severity="warning" sx={{ mb: 4, width: '100%' }}>
            <Typography variant="body2">
              Account registration is restricted and managed by administrators only. 
              If you need access to this platform, please contact your system administrator directly.
            </Typography>
          </Alert>

          <Box sx={{ width: '100%', mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" gutterBottom textAlign="center">
              Contact Information:
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: '400px', textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                To gain access to this platform, please contact your system administrator or IT department directly.
              </Typography>
              
              <Typography variant="body2" color="text.secondary">
                Only authorized administrators can create new user accounts for security purposes.
              </Typography>
            </Box>
            
            <Box sx={{ width: '304px', mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleGoToLogin}
                sx={{ 
                  py: 1.2,
                  height: '45px',
                  bgcolor: '#485869', 
                  color: '#ffffff',
                  fontFamily: '"Sofia Pro Regular", "Sofia Pro", sans-serif',
                  fontSize: '16px',
                  fontWeight: 500,
                  borderRadius: '5px',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: '#3A4754',
                  },
                  boxShadow: 'none',
                }}
              >
                Back to Login
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default Register;